import React from "react";
import { ChevronDown, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Wraps a panel with a slim grip bar that can minimize it. The body animates
 * height/opacity with a spring; children stay mounted so their state survives.
 * Collapsed state is owned by the parent (cloud-persisted via settings).
 */
const CollapsiblePanel = ({
  panelKey,
  title,
  icon: Icon,
  count,
  collapsed,
  onToggle,
  onAi,
  aiName,
  children,
}) => (
  // No framer-motion `layout` projection here: it animates size with a scaleY
  // transform on this wrapper, and sticky notes pinned inside a panel are
  // positioned from that panel's box — a transform would shift them off it
  // (notes drifting outside the Sticky Notes board). The body below animates
  // its own height, which is all the panel actually needs.
  <div className="rounded-3xl card-shadow">
    <div
      className={`flex items-center justify-between gap-3 px-4 transition-all ${
        collapsed
          ? "py-3 bg-gray-800 border-2 border-cyan-500/30 rounded-3xl"
          : "py-1.5 bg-gray-800/70 border-2 border-b-0 border-cyan-500/20 rounded-t-3xl"
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {Icon && (
          <Icon
            className={`text-yellow-400 flex-shrink-0 transition-all ${
              collapsed ? "w-5 h-5" : "w-3.5 h-3.5 opacity-70"
            }`}
          />
        )}
        <span
          className={`font-semibold text-yellow-400 truncate transition-all ${
            collapsed ? "text-base" : "text-[11px] uppercase tracking-wider opacity-70"
          }`}
        >
          {title}
        </span>
        {typeof count === "number" && (
          <span className="text-[11px] text-gray-400 bg-gray-700/60 rounded-full px-2 py-0.5 tabular-nums">
            {count}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
      {onAi && (
        <button
          type="button"
          onClick={() => onAi(panelKey)}
          className="tap px-2.5 py-1.5 rounded-xl text-xs font-semibold text-purple-300 hover:bg-gray-700/60 flex items-center gap-1"
          title={`Ask ${aiName || "the AI"} about this panel`}
          aria-label={`Ask ${aiName || "the AI"} about ${title}`}
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Ask AI</span>
        </button>
      )}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-controls={`panel-${panelKey}`}
        className="tap px-3 py-1.5 rounded-xl text-xs font-semibold text-cyan-300 hover:bg-gray-700/60 flex items-center gap-1"
      >
        <motion.span
          animate={{ rotate: collapsed ? 0 : 180 }}
          transition={{ type: "spring", stiffness: 300, damping: 24 }}
          className="inline-flex"
        >
          <ChevronDown className="w-4 h-4" />
        </motion.span>
        {collapsed ? "Expand" : "Minimize"}
      </button>
      </div>
    </div>
    <motion.div
      id={`panel-${panelKey}`}
      initial={false}
      animate={
        collapsed
          ? { height: 0, opacity: 0, y: -10, scale: 0.985 }
          : { height: "auto", opacity: 1, y: 0, scale: 1 }
      }
      transition={{
        height: { type: "spring", stiffness: 240, damping: 30 },
        opacity: { duration: 0.22 },
        y: { duration: 0.28 },
        scale: { duration: 0.28 },
      }}
      style={{ overflow: "hidden" }}
      aria-hidden={collapsed}
    >
      <div className={`xz-collapsible-body ${collapsed ? "pointer-events-none" : ""}`}>
        {children}
      </div>
    </motion.div>
  </div>
);

export default CollapsiblePanel;
